import isNil from 'lodash/isNil';
import moment from 'moment';
import {Component} from 'panel';

import {filterAttributes} from 'common/util';
import {pluralize} from 'common/util/string';
import {propagateEvent} from 'common/util/dom';
import {FEATURE_GATES_UNLIMITED} from 'common/report/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {normalizeDateRange, normalizeDateStrings, parseDate} from 'common/util/date';

import {getDateLimitedTooltipText, getMinDateStr} from '../utils';
import template from './index.jade';
import css from './index.inline.styl';
import './date-input';

const INPUT = {
  NONE: null,
  FROM: `from`,
  TO: `to`,
};

export class CalendarInput extends Component {
  static get observedAttributes() {
    return Component.observedAttributes.concat([
      `from`,
      `is-double-calendar`,
      `is-range`,
      `max-date`,
      `max-history-days`,
      `min-days-apart`,
      `show-upsell`,
      `to`,
    ]);
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(...arguments);
    switch (attr) {
      case `from`: {
        this.update({from: newValue});
        break;
      }
      case `is-range`: {
        this.update({isRangePicker: this.isAttributeEnabled(attr)});
        break;
      }
      case `max-history-days`: {
        const maxHistoryDays = this.getNumberAttribute(attr);
        this.update({
          maxHistoryDays,
          ...(maxHistoryDays !== FEATURE_GATES_UNLIMITED && {
            minDate: getMinDateStr({maxHistoryDays}),
          }),
        });
        break;
      }
      case `show-upsell`: {
        this.update({showUpsell: this.isAttributeEnabled(attr)});
        break;
      }
      case `to`: {
        this.update({to: newValue});
        break;
      }
      default: {
        break;
      }
    }
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        error: {},
        focused: INPUT.NONE,
        mousedOver: INPUT.NONE,
        from: ``,
        isRangePicker: false,
        showUpsell: false,
        to: ``,
      },
      helpers: {
        getCalendarAttrs: () => {
          let attrs = {
            'is-double-calendar': this.isAttributeEnabled(`is-double-calendar`),
            'min-date': this.state.minDate,
            'max-date': this.getAttribute(`max-date`),
          };

          const {isRangePicker, focused, from, to} = this.state;
          if (isRangePicker) {
            attrs = {
              ...attrs,
              'is-from-date-focused': focused === INPUT.FROM,
              'is-range': true,
              'is-to-date-focused': focused === INPUT.TO,
              'selected-from-date': from,
              'selected-to-date': to,
            };
          } else {
            attrs = {
              ...attrs,
              'selected-date': from,
            };
          }

          return filterAttributes(attrs);
        },
        selectedDate: (ev) => {
          if (this.state.isRangePicker) {
            let {from = this.state.from, to = this.state.to} = ev.detail;
            [from, to] = this.normalizeDateStrings([from, to]);
            this.selectDateRange(from, to);
          } else {
            this.selectSingleDate(ev.detail.date);
          }
        },
        selectedFixedRangeDate: (ev) => {
          if (this.state.focused === INPUT.NONE) {
            this.update({focused: INPUT.FROM});
          }
          const {numDaysApart, maxDateStr} = this.getFixedRangeAttributes();
          let from, to, errorMsg;
          if (!this.state.isRangePicker) {
            ({errorMsg, from, to} = this.getChoosingDate({
              numDaysApart,
              maxDateStr,
              compareTimeStr: ev.detail.date,
              isFrom: true,
            }));
            if (errorMsg) {
              return this.update({error: {from: errorMsg}});
            }
            return this.selectSingleDate(from);
          } else if (this.state.focused === INPUT.TO) {
            ({errorMsg, from, to} = this.getChoosingDate({
              numDaysApart,
              maxDateStr,
              compareTimeStr: ev.detail.to,
              isFrom: false,
            }));
            if (errorMsg) {
              return this.update({error: {to: errorMsg}});
            }
          } else {
            ({errorMsg, from, to} = this.getChoosingDate({
              numDaysApart,
              maxDateStr,
              compareTimeStr: ev.detail.from,
              isFrom: true,
            }));
            if (errorMsg) {
              return this.update({error: {from: errorMsg}});
            }
          }
          [from, to] = this.normalizeDateStrings([from, to]);
          this.selectDateRange(from, to);
        },
        changedFrom: (ev) => {
          if (this.state.isRangePicker) {
            const [from, to] = normalizeDateRange(ev.detail, this.state.to, `from`);
            this.selectDateRange(from, to);
          } else {
            this.selectSingleDate(ev.detail);
          }
        },
        changedFixedRangeFrom: (ev) => {
          const {numDaysApart, maxDateStr} = this.getFixedRangeAttributes();
          let {errorMsg, from, to} = this.getChoosingDate({
            numDaysApart,
            maxDateStr,
            compareTimeStr: ev.detail,
            isFrom: true,
          });
          if (errorMsg) {
            return this.update({error: {from: errorMsg}});
          }
          [from, to] = this.normalizeDateStrings([from, to]);
          if (this.state.isRangePicker) {
            this.selectDateRange(from, to);
          } else {
            this.selectSingleDate(from);
          }
        },
        changedTo: (ev) => {
          const [from, to] = normalizeDateRange(this.state.from, ev.detail, `to`);
          this.selectDateRange(from, to);
        },
        changedFixedRangeTo: (ev) => {
          const {numDaysApart, maxDateStr} = this.getFixedRangeAttributes();
          let {errorMsg, from, to} = this.getChoosingDate({
            numDaysApart,
            maxDateStr,
            compareTimeStr: ev.detail,
            isFrom: false,
          });
          if (errorMsg) {
            return this.update({error: {to: errorMsg}});
          }
          [from, to] = this.normalizeDateStrings([from, to]);
          this.selectDateRange(from, to);
        },
        blurredFrom: () => {
          if (this.state.focused === INPUT.FROM) {
            this.update({focused: INPUT.NONE});
          }
        },
        focusedFrom: () => {
          this.update({focused: INPUT.FROM});
        },
        mouseenterFrom: () => {
          this.update({mousedOver: INPUT.FROM});
        },
        mouseleaveFrom: () => {
          this.update({mousedOver: INPUT.NONE});
        },
        blurredTo: () => {
          if (this.state.focused === INPUT.TO) {
            this.update({focused: INPUT.NONE});
          }
        },
        focusedTo: () => {
          this.update({focused: INPUT.TO});
        },
        mouseenterTo: () => {
          this.update({mousedOver: INPUT.TO});
        },
        mouseleaveTo: () => {
          this.update({mousedOver: INPUT.NONE});
        },
        propagateEvent: (ev) => {
          propagateEvent(this, ev);
        },
        isFromActive: () => {
          return this.state.focused === INPUT.FROM || this.state.mousedOver === INPUT.FROM;
        },
        isToActive: () => {
          return this.state.focused === INPUT.TO || this.state.mousedOver === INPUT.TO;
        },
        isFixedRange: () => this.isFixedRange(),
      },
    };
  }

  emitChange() {
    const {from, to} = this.state;
    this.dispatchEvent(new CustomEvent(`change`, {detail: {from, to}}));
  }

  selectDateRange(from, to) {
    const fromDate = parseDate(from);
    if (!fromDate || (to && !parseDate(to))) {
      return;
    } else if (fromDate < parseDate(this.state.minDate)) {
      this.update({error: {from: this.getDateLimitedError()}});
      return;
    }

    this.update({
      error: {},
      from,
      to: to || ``,
    });
    if (from && to) {
      const daysApart = this.numDaysApart(from, to);
      const minDaysApart = Number(this.getAttribute(`min-days-apart`) || 0);
      if (daysApart < minDaysApart) {
        this.update({
          error: {to: `Dates must be at least ${minDaysApart} ${pluralize(`day`, minDaysApart, `days`)} apart.`},
        });
      } else {
        this.emitChange();
      }
    }
  }

  selectSingleDate(date) {
    const [from] = this.normalizeDateStrings([date]);
    const fromDate = parseDate(from);
    if (fromDate && fromDate < parseDate(this.state.minDate)) {
      this.update({error: {from: this.getDateLimitedError()}});
    } else if (fromDate) {
      this.update({from, error: {}});
      this.emitChange();
    }
  }

  normalizeDateStrings(dates) {
    // Allow picking dates in the future. mp-calendar disables dates past the max date, so we don't have to
    // constrain that here.
    return normalizeDateStrings(dates, {allowFuture: true});
  }

  getDateLimitedError() {
    return getDateLimitedTooltipText({
      maxHistoryDays: this.state.maxHistoryDays,
      showUpsell: this.state.showUpsell,
      beforeEpoch: isNil(this.state.minDate),
    });
  }

  getFixedRangeAttributes() {
    const numDaysApart = Number(this.getAttribute(`fixed-range-days-apart`));
    const maxDateStr = this.getAttribute(`max-date`);
    return {numDaysApart, maxDateStr};
  }

  isFixedRange() {
    return this.getAttribute(`fixed-range-days-apart`) !== null;
  }

  numDaysApart(from, to) {
    return moment(to).diff(moment(from), `days`);
  }

  getChoosingDate({maxDateStr, compareTimeStr, isFrom, numDaysApart} = {}) {
    const compareTime = moment(compareTimeStr);
    let from, to;
    if (isFrom) {
      from = compareTime;
      to = from.clone().add(numDaysApart, `days`);
    } else {
      to = compareTime;
      from = to.clone().subtract(numDaysApart, `days`);
    }
    if (to.diff(moment(maxDateStr), `days`) >= 0) {
      return {errorMsg: `Select a date range`};
    }
    return {from: from.format(`YYYY-MM-DD`), to: to.format(`YYYY-MM-DD`)};
  }

  focusFromInput() {
    if (this.initialized) {
      this.fromInput.focus();
    }
  }

  selectFromInput() {
    if (this.initialized) {
      this.fromInput.select();
    }
  }

  focusToInput() {
    if (this.initialized) {
      this.toInput.focus();
    }
  }

  selectToInput() {
    if (this.initialized) {
      this.toInput.select();
    }
  }

  get fromInput() {
    return this.el.querySelector(`mp-date-input.from-date-input`);
  }

  get toInput() {
    return this.el.querySelector(`mp-date-input.to-date-input`);
  }
}

export const CalendarInputEl = defineAndMakeJsxFactory(`mp-calendar-input`, CalendarInput);
export const CalendarInputReact = panel2react(`mp-calendar-input`, CalendarInput);
